import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { CatalogForm } from '../../../components/CatalogForm/CatalogForm';
import { Validators } from '../../../components/CatalogForm/validators';
import { ChoferService } from '../../../services/ChoferService';

const ChoferForm = ({ id: propId, modal = false, onSave, back = true }) => {
  const routeParams = useParams() || {};
  const currentId = propId || routeParams.id || null;

  // Options states
  const [estadosOptions, setEstadosOptions] = useState([]);
  const [ciudadesOptions, setCiudadesOptions] = useState([]);
  const [coloniasOptions, setColoniasOptions] = useState([]);

  // Load initial catalog options (estados)
  useEffect(() => {
    let isMounted = true;
    const loadInitialData = async () => {
      const res = await ChoferService.obtenerFormGenerales();
      if (isMounted && res && Array.isArray(res.estados)) {
        setEstadosOptions(res.estados.map((e) => ({ value: e.id, label: e.estado })));
      }
    };
    loadInitialData();
    return () => {
      isMounted = false;
    };
  }, []);

  // Pre-load data in edit mode
  const loadData = useCallback(async (recordId) => {
    const data = await ChoferService.obtenerPorId(recordId);
    if (data) {
      const cp = data.codigo_postal || data.chofer_codigo_postal;
      if (cp) {
        data.codigo_postal = cp;
        const res = await ChoferService.obtenerFormGenerales({ cp });
        if (res && res.direccion) {
          const { direccion } = res;
          if (Array.isArray(direccion.colonias)) {
            setColoniasOptions(direccion.colonias.map((c) => ({ value: c.id, label: c.colonia })));
          }
          if (Array.isArray(direccion.ciudades)) {
            setCiudadesOptions(direccion.ciudades.map((c) => ({ value: c.id, label: c.ciudad })));
          }
          if (direccion.estado && direccion.estado.id) {
            data.estado = direccion.estado.id;
          }
          if (direccion.ciudad && direccion.ciudad.id) {
            data.ciudad = direccion.ciudad.id;
          }
        }
      }
    }
    return data;
  }, []);

  // Handle CP reactive change
  const handleCpChange = useCallback(async (val, { setFieldValue }) => {
    if (!val || String(val).trim().length < 5) return;
    const cleanCp = String(val).trim().substring(0, 5);
    const res = await ChoferService.obtenerFormGenerales({ cp: cleanCp });
    if (res && res.direccion) {
      const { direccion } = res;
      if (Array.isArray(direccion.colonias)) {
        const mappedCols = direccion.colonias.map((c) => ({ value: c.id, label: c.colonia }));
        setColoniasOptions(mappedCols);
        if (mappedCols.length === 1) {
          setFieldValue('chofer_id_colonia', mappedCols[0].value);
        } else {
          setFieldValue('chofer_id_colonia', '');
        }
      }
      if (Array.isArray(direccion.ciudades)) {
        setCiudadesOptions(direccion.ciudades.map((c) => ({ value: c.id, label: c.ciudad })));
      }
      if (direccion.estado && direccion.estado.id) {
        setFieldValue('estado', direccion.estado.id);
      }
      if (direccion.ciudad && direccion.ciudad.id) {
        setFieldValue('ciudad', direccion.ciudad.id);
      }
    }
  }, []);

  // Handle Estado change to reload ciudades
  const handleEstadoChange = useCallback(async (val, { setFieldValue }) => {
    if (!val) {
      setCiudadesOptions([]);
      setFieldValue('ciudad', '');
      return;
    }
    const res = await ChoferService.obtenerFormGenerales({ e: val });
    if (res && Array.isArray(res.ciudades)) {
      setCiudadesOptions(res.ciudades.map((c) => ({ value: c.id, label: c.ciudad })));
    }
  }, []);

  // Submit handler
  const handleSubmit = useCallback(async (values) => {
    const payload = {
      ...values,
      id: currentId,
      chofer_rfc: values.chofer_rfc ? String(values.chofer_rfc).trim().toUpperCase() : values.chofer_rfc,
      chofer_id_colonia: values.chofer_id_colonia ? Number(values.chofer_id_colonia) : values.chofer_id_colonia,
    };
    const res = await ChoferService.guardar(payload);
    if (res && !res.error && onSave) {
      onSave(res.data || res);
    }
    return res;
  }, [currentId, onSave]);

  // Form field configurations
  const fields = useMemo(() => [
    {
      name: 'chofer_nombre',
      label: 'Nombre',
      type: 'text',
      col: 6,
      required: true,
      placeholder: 'Ej. Juan',
      validators: [Validators.required('El nombre del chofer es obligatorio')],
    },
    {
      name: 'chofer_apellido_paterno',
      label: 'Apellido Paterno',
      type: 'text',
      col: 6,
      required: true,
      placeholder: 'Ej. Hernández',
      validators: [Validators.required('El apellido paterno es obligatorio')],
    },
    {
      name: 'chofer_apellido_materno',
      label: 'Apellido Materno',
      type: 'text',
      col: 6,
      required: true,
      placeholder: 'Ej. López',
      validators: [Validators.required('El apellido materno es obligatorio')],
    },
    {
      name: 'chofer_rfc',
      label: 'RFC (Persona Física)',
      type: 'text',
      col: 6,
      required: true,
      placeholder: 'Ej. HELJ850101XYZ',
      validators: [
        Validators.required('El RFC es obligatorio'),
        Validators.rfcFisica('RFC de persona física no válido (formato: ABCD-YYMMDD-XXX)'),
      ],
    },
    {
      name: 'chofer_numero_licencia',
      label: 'Número de Licencia',
      type: 'text',
      col: 6,
      required: true,
      placeholder: 'Ej. LIC-12345678',
      validators: [Validators.required('El número de licencia es obligatorio')],
    },
    {
      name: 'sec_domicilio',
      type: 'section',
      label: 'Domicilio del Chofer',
    },
    {
      name: 'chofer_calle',
      label: 'Calle',
      type: 'text',
      col: 6,
      required: true,
      placeholder: 'Ej. Av. Hidalgo',
      validators: [Validators.required('La calle es obligatoria')],
    },
    {
      name: 'chofer_numero',
      label: 'Número Exterior',
      type: 'text',
      col: 6,
      required: true,
      placeholder: 'Ej. 123-A',
      validators: [Validators.required('El número exterior es obligatorio')],
    },
    {
      name: 'codigo_postal',
      label: 'Código Postal',
      type: 'text',
      col: 6,
      required: true,
      placeholder: 'Ej. 44100',
      onChange: handleCpChange,
      validators: [
        Validators.required('El código postal es obligatorio'),
        Validators.minLength(5, 'El código postal debe tener 5 dígitos'),
        Validators.maxLength(5, 'El código postal debe tener 5 dígitos'),
      ],
    },
    {
      name: 'chofer_id_colonia',
      label: 'Colonia',
      type: 'select',
      col: 6,
      required: true,
      options: coloniasOptions,
      placeholder: 'Seleccionar colonia...',
      validators: [Validators.required('La colonia es obligatoria')],
    },
    {
      name: 'estado',
      label: 'Estado',
      type: 'select',
      col: 6,
      required: true,
      options: estadosOptions,
      placeholder: 'Seleccionar estado...',
      onChange: handleEstadoChange,
      validators: [Validators.required('El estado es obligatorio')],
    },
    {
      name: 'ciudad',
      label: 'Ciudad',
      type: 'select',
      col: 6,
      required: true,
      options: ciudadesOptions,
      placeholder: 'Seleccionar ciudad...',
      validators: [Validators.required('La ciudad es obligatoria')],
    },
  ], [coloniasOptions, estadosOptions, ciudadesOptions, handleCpChange, handleEstadoChange]);

  return (
    <CatalogForm
      title="Chofer"
      subtitle="Administración de operadores de transporte y sus licencias"
      fields={fields}
      initialValues={{
        chofer_nombre: '',
        chofer_apellido_paterno: '',
        chofer_apellido_materno: '',
        chofer_rfc: '',
        chofer_numero_licencia: '',
        chofer_calle: '',
        chofer_numero: '',
        codigo_postal: '',
        chofer_id_colonia: '',
        ciudad: '',
        estado: '',
      }}
      id={currentId}
      loadData={loadData}
      onSubmit={handleSubmit}
      backUrl={modal || !back ? undefined : '/Catalogo/Choferes'}
    />
  );
};

export default ChoferForm;
